import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { searchCompany } from '../../../../../api';
import './search_bar.component.css';

const SearchBar = React.forwardRef((props, ref) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [selectedResult, setSelectedResult] = useState({});
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showSelected, setShowSelected] = useState(false);
  const selectedResultElem = useRef();

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    setShowSelected(false);
  };

  const handleKeyboardNav = (e) => {
    e.stopPropagation();
    switch (e.keyCode) {
      case 40:
        e.preventDefault();
        if (selectedIdx < searchResults.length - 1)
          setSelectedIdx(selectedIdx + 1);
        break;
      case 38:
        e.preventDefault();
        if (selectedIdx > 0) setSelectedIdx(selectedIdx - 1);
        break;
      case 13:
        e.preventDefault();
        props.onSearchClick(selectedResult);
        setSearchResults([]);
        setSearchTerm('');
        setSelectedIdx(0);
        setSelectedResult({});
        break;
      default:
        break;
    }
    if (e.keyCode === 40 || e.keyCode === 38) setShowSelected(true);
  };

  const getHighlightedSuggestion = (str) => {
    const searchTermLength = searchTerm.length;
    str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    let searchText = searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const searchTermExp = new RegExp(searchText, 'gi');
    let matchedIdxs = [...str.matchAll(searchTermExp)].map(
      (matched) => matched['index'],
    );
    let splits = [];
    _.forEach(matchedIdxs, (idx) => {
      splits.push(idx);
      splits.push(idx + searchTermLength);
    });
    splits = [0, ...splits];
    return splits.map((split, idx, splits) =>
      str.slice(split, splits[idx + 1]),
    );
  };

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }
    const fetchResults = async () => {
      const api = props.api ?? searchCompany;
      const results = await api(searchTerm);
      if (!results.data.length) {
        setSearchResults([
          { nocomp: props.noSuggestionText ?? 'Sorry! No companies found!' },
        ]);
        return;
      }
      let data = results.data;
      if (props.existingComps && props.existingComps.length > 0) {
        data = data.filter((d) => props.existingComps.indexOf(d.fincode) < 0);
      }
      if (props.isGroupBy) {
        // group by type company/mutual_fund/index(accord,tji)
        let filteredResults = _.groupBy(data, 'index_type');
        if (filteredResults['accord'] || filteredResults['tji']) {
          filteredResults['index'] = _.concat(
            filteredResults['tji'] || [],
            filteredResults['accord'] || [],
          );
          delete filteredResults['accord'];
          delete filteredResults['tji'];
        }
        let formattedResults = [];
        const groupNameMap = {
          company: 'companies',
          mutual_fund: 'mutual funds',
          index: 'indices',
          etf: 'etf',
        };
        _.forOwn(filteredResults, (value, key) => {
          let groupHeader = { type: 'header', name: groupNameMap[key] };
          formattedResults.push(groupHeader);
          formattedResults = _.concat(formattedResults, value);
        });
        data = formattedResults;
      }
      setSearchResults(data);
    };
    const debounce = setTimeout(() => fetchResults(), 300);
    // Return a function to useEffect for cleaning up on next re-render
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  useEffect(() => {
    if (searchResults.length) {
      setSelectedResult(searchResults[0]);
    }
  }, [searchResults]);

  useEffect(() => {
    if (searchResults.length) setSelectedResult(searchResults[selectedIdx]);
  }, [selectedIdx]);

  useEffect(() => {
    if (selectedResultElem.current)
      selectedResultElem.current.scrollIntoView({ block: 'nearest' });
  }, [selectedResult]);

  return (
    <div
      className={`add-tx-search_container pl ${
        props.containerClass ? props.containerClass : ''
      }`}
    >
      <input
        ref={ref}
        type="text"
        spellCheck="false"
        placeholder={props.placeholder}
        className={`search_input-add ${props.padding ? 'py-2' : ''} ${
          props.inputClass ? props.inputClass : ''
        }`}
        onBlur={() => {
          setTimeout(() => {
            setSearchResults([]);
            setSelectedIdx(0);
            setSelectedResult({});
            props.onBlur ? props.onBlur() : null;
          }, 300);
        }}
        value={
          showSelected && selectedResult
            ? selectedResult.short_name || selectedResult.compname
            : searchTerm
        }
        onChange={handleChange}
        onKeyDown={(e) => handleKeyboardNav(e)}
      />
      {searchResults && (
        <ul className="search_results" style={props.suggestionsPosition}>
          {searchResults.length >= 1 &&
          (searchResults[0].compname || searchResults[0].name) ? (
            searchResults.map((r, idx) =>
              r.type === 'header' ? (
                <li key={r.name} className="grp-header">
                  {r.name}
                </li>
              ) : (
                <li
                  className={`search_item p-2 ${
                    idx === selectedIdx ? 'background-color' : ''
                  }`}
                  key={r.id}
                  onClick={() => {
                    setSearchResults([]);
                    setSearchTerm('');
                    setSelectedIdx(0);
                    setSelectedResult({});
                    props.onSearchClick(r);
                  }}
                  onMouseOver={() => setSelectedIdx(idx)}
                  ref={r.id === selectedResult.id ? selectedResultElem : null}
                >
                  <span>
                    {getHighlightedSuggestion(r.short_name || r.compname).map(
                      (str, idx) => {
                        if (
                          str.toLowerCase() === searchTerm.toLocaleLowerCase()
                        )
                          return <strong key={idx}>{str}</strong>;
                        else return str;
                      },
                    )}
                  </span>
                </li>
              ),
            )
          ) : searchResults.length === 1 ? (
            <li className="search_item p-2">{searchResults[0].nocomp}</li>
          ) : null}
        </ul>
      )}
    </div>
  );
});

export default SearchBar;
